import { useCallback } from 'react';
import { useAlert } from 'react-alert';
import { useTranslation } from 'react-i18next';

import { useActions } from '../../../../../../../hooks/useActions';
import { useModalDetails } from '../../../../../../../hooks/useModalDetails';
import { useProcessing } from '../../../../../../../hooks/useProcessing';
import { useCancellationToken } from '../../../../../../../hooks/useTokenCancellation';
import { updateModalState } from '../../../../../../../store/redux-slices/modals';
import { ORGANIZATION_CONTRACT_NOTES_ADDING_PROCESSING } from '../../../../../../../store/redux-slices/processes/constants';
import { addOrganizationContractNotes } from '../../../../../../../store/saga-slices/contracts';
import { IAddOrganizationContractNotesPayload } from '../../../../../../../store/saga-slices/contracts/models';
import {
  useApiResponseError,
  useFormErrors,
} from '../../../../../../../styledComponents/FormErrorProvider/FormErrorProvider.hooks';
import { validateNotesForm } from '../../../../../../../styledComponents/Forms/NotesForm/notesForm.validation';
import { ADD_CONTRACT_NOTES_MODAL_NAME } from '../addContractNotesModal.const';
import { AddOrganizationContractNotesModalDetails } from './addContractNotesModalForm.models';

export interface IUseAddContractModalFormProps {
  onNotesAdded?: () => void;
}

export const useAddContractNotes = ({
  onNotesAdded,
}: IUseAddContractModalFormProps) => {
  const cancellationToken = useCancellationToken();
  const alert = useAlert();
  const { t } = useTranslation();

  const UpdateModalState = useActions(updateModalState);
  const AddOrganizationContractNotes = useActions(addOrganizationContractNotes);

  const isProcessing = useProcessing(
    ORGANIZATION_CONTRACT_NOTES_ADDING_PROCESSING,
  );

  const { validateErrors, addErrors } = useFormErrors();

  const { handleResponseError } = useApiResponseError({
    externalAddErrors: addErrors,
  });

  const {
    contractId,
    organizationId,
  }: AddOrganizationContractNotesModalDetails = useModalDetails(
    ADD_CONTRACT_NOTES_MODAL_NAME,
  );

  const closeModalHandler = useCallback(() => {
    UpdateModalState(ADD_CONTRACT_NOTES_MODAL_NAME);
  }, [UpdateModalState]);

  const handleSubmit = useCallback(
    (values: { notes: string }) => {
      const payload: IAddOrganizationContractNotesPayload = {
        organizationId,
        contractId,
        notes: values.notes,
        cancellationToken,
        success: () => {
          alert.success(t('alerts.contract-notes-added-success'));
          closeModalHandler();
          onNotesAdded?.();
        },
        error: (err: any) => {
          handleResponseError(err, values);
          alert.error(t('alerts.contract-notes-added-fails'));
        },
      };
      AddOrganizationContractNotes(payload);
    },
    [
      AddOrganizationContractNotes,
      alert,
      cancellationToken,
      closeModalHandler,
      contractId,
      handleResponseError,
      onNotesAdded,
      organizationId,
      t,
    ],
  );

  const validateData = useCallback(
    (data: IAddOrganizationContractNotesPayload) =>
      validateNotesForm(data, validateErrors(data)),
    [validateErrors],
  );

  return {
    closeModalHandler,
    handleSubmit,
    isProcessing,
    validateData,
  };
};
